import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useCart } from '../context/CartContext';
import {
  createStripeIntent, confirmStripeOrder,
  createPayPalOrder, capturePayPalOrder,
} from '../services/api';
import './Checkout.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function hasAddress(form) {
  return Boolean(form.name && form.email && form.street && form.city && form.state && form.zip);
}

function addressMatches(savedAddress, form) {
  if (!savedAddress) return false;
  return ['street', 'city', 'state', 'zip', 'country'].every((key) =>
    (savedAddress[key] || '').trim().toUpperCase() === (form[key] || '').trim().toUpperCase()
  );
}

// ── Stripe Form ───────────────────────────────────────────────────────────────
function StripeForm({ orderData, onSuccess, canSubmit }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');

    try {
      const { data: intentData } = await createStripeIntent({
        items: orderData.items.map(i => ({ id: i.id, qty: i.qty })),
        shippingCost: orderData.shippingCost,
        customerEmail: orderData.email,
        address: {
          street: orderData.street,
          city: orderData.city,
          state: orderData.state,
          zip: orderData.zip,
          country: orderData.country,
        },
      });

      const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(
        intentData.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: { name: orderData.name, email: orderData.email },
          },
        }
      );

      if (stripeErr) {
        setError(stripeErr.message);
        setProcessing(false);
        return;
      }

      const { data } = await confirmStripeOrder({
        paymentIntentId: paymentIntent.id,
        orderData,
      });

      onSuccess(data.orderNumber);
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="stripe-card-wrapper">
        <CardElement options={{ style: { base: { fontSize: '16px', color: '#1e293b' } } }} />
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={processing || !stripe || !canSubmit}>
        {processing ? 'Processing...' : `Pay $${orderData.total.toFixed(2)}`}
      </button>
    </form>
  );
}

// ── Main Checkout ─────────────────────────────────────────────────────────────
export default function Checkout() {
  const { items, subtotal, clearCart, shippingQuote } = useCart();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [form, setForm] = useState({
    name: '',
    email: '',
    street: shippingQuote?.address?.street || '',
    city: shippingQuote?.address?.city || '',
    state: shippingQuote?.address?.state || '',
    zip: shippingQuote?.address?.zip || '',
    country: shippingQuote?.address?.country || 'US',
  });

  const shippingSelectionValid = Boolean(
    shippingQuote?.selectedRate &&
    !shippingQuote?.stale &&
    addressMatches(shippingQuote?.address, form)
  );
  const selectedRate = shippingSelectionValid ? shippingQuote.selectedRate : null;
  const taxAmount = shippingSelectionValid ? Number(shippingQuote?.taxAmount || 0) : 0;
  const totalsReady = shippingSelectionValid && Boolean(shippingQuote?.totalsPreviewed);
  const addressComplete = hasAddress(form);
  const shippingCost = selectedRate?.rate || 0;
  const total = subtotal + shippingCost + taxAmount;
  const canPay = addressComplete && Boolean(selectedRate) && totalsReady;
  const paymentBlockedReason = !addressComplete
    ? 'Complete your shipping address to continue.'
    : !shippingQuote?.selectedRate
      ? 'Choose a live shipping rate in the cart before continuing to payment.'
      : shippingQuote?.stale
        ? 'Your cart changed after the shipping quote was created. Return to the cart to refresh delivery pricing.'
        : !shippingQuote?.totalsPreviewed
          ? 'Return to the cart to refresh estimated tax before continuing to payment.'
        : !selectedRate
          ? 'Your shipping address changed. Return to the cart to refresh the rate for this address.'
          : '';

  const orderData = {
    ...form,
    items,
    subtotal,
    shippingCost,
    taxAmount,
    total,
    shipmentId: selectedRate?.shipmentId,
    rateId: selectedRate?.id,
  };

  const onSuccess = (orderNumber) => {
    clearCart();
    navigate(`/order/${orderNumber}`);
  };

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="container" style={{ padding: '2rem 1.5rem' }}>
      <div className="page-header"><h1>Checkout</h1></div>

      <div className="checkout-layout">
        {/* Left: Form */}
        <div>
          <div className="checkout-progress card" style={{ marginBottom: '1.5rem' }}>
            <div className={`checkout-step ${addressComplete ? 'complete' : 'active'}`}>
              <span className="checkout-step-index">1</span>
              <div>
                <strong>Shipping details</strong>
                <p>Tell us where the order should go.</p>
              </div>
            </div>
            <div className={`checkout-step ${shippingSelectionValid ? 'complete' : paymentBlockedReason ? 'active' : ''}`}>
              <span className="checkout-step-index">2</span>
              <div>
                <strong>Delivery locked in</strong>
                <p>Shipping is selected in the cart and carried into checkout.</p>
              </div>
            </div>
            <div className={`checkout-step ${canPay ? 'active' : ''}`}>
              <span className="checkout-step-index">3</span>
              <div>
                <strong>Payment</strong>
                <p>Card or PayPal, once shipping is locked in.</p>
              </div>
            </div>
          </div>

          {selectedRate ? (
            <div className="card checkout-shipping-lockup" style={{ marginBottom: '1.5rem' }}>
              <div>
                <h2 className="section-title">📦 Shipping Selection</h2>
                <p className="checkout-shipping-copy">Your live rate is already selected from the cart.</p>
              </div>
              <div className="summary-shipping-chip">
                {selectedRate.carrier} {selectedRate.service} · ${selectedRate.rate.toFixed(2)}
              </div>
            </div>
          ) : (
            <div className="card checkout-shipping-lockup" style={{ marginBottom: '1.5rem' }}>
              <div>
                <h2 className="section-title">📦 Shipping Selection</h2>
                <p className="checkout-shipping-copy">Shipping rates now live in the cart so customers can compare costs before checkout.</p>
              </div>
              <button className="btn btn-secondary" onClick={() => navigate('/cart')}>
                Return to Cart for Shipping
              </button>
            </div>
          )}

          {/* Shipping Address */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2 className="section-title">📍 Shipping Address</h2>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-control" value={form.name} onChange={e => setForm((current) => ({ ...current, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-control" type="email" value={form.email} onChange={e => setForm((current) => ({ ...current, email: e.target.value }))} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Street Address</label>
              <input className="form-control" value={form.street} onChange={e => updateField('street', e.target.value)} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-control" value={form.city} onChange={e => updateField('city', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input className="form-control" maxLength={2} placeholder="CA" value={form.state} onChange={e => updateField('state', e.target.value.toUpperCase())} required />
              </div>
              <div className="form-group">
                <label className="form-label">ZIP</label>
                <input className="form-control" value={form.zip} onChange={e => updateField('zip', e.target.value)} required />
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="card">
            <h2 className="section-title">💳 Payment</h2>
            {paymentBlockedReason && (
              <div className="alert alert-info checkout-payment-alert">{paymentBlockedReason}</div>
            )}

            <div className="payment-tabs">
              <button
                className={`payment-tab ${paymentMethod === 'stripe' ? 'active' : ''}`}
                disabled={!canPay}
                onClick={() => setPaymentMethod('stripe')}
              >
                💳 Credit/Debit Card
              </button>
              <button
                className={`payment-tab ${paymentMethod === 'paypal' ? 'active' : ''}`}
                disabled={!canPay}
                onClick={() => setPaymentMethod('paypal')}
              >
                🅿 PayPal
              </button>
            </div>

            {paymentMethod === 'stripe' && (
              <Elements stripe={stripePromise}>
                <StripeForm orderData={orderData} onSuccess={onSuccess} canSubmit={canPay} />
              </Elements>
            )}

            {paymentMethod === 'paypal' && (
              <PayPalScriptProvider options={{ 'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: 'USD' }}>
                {canPay ? (
                  <PayPalButtons
                    style={{ layout: 'vertical' }}
                    createOrder={() =>
                      createPayPalOrder({
                        items: items.map(i => ({ id: i.id, qty: i.qty })),
                        shippingCost,
                        address: {
                          street: form.street,
                          city: form.city,
                          state: form.state,
                          zip: form.zip,
                          country: form.country,
                        },
                      })
                        .then(r => r.data.orderId)
                    }
                    onApprove={(data) =>
                      capturePayPalOrder({ paypalOrderId: data.orderID, orderData })
                        .then(r => onSuccess(r.data.orderNumber))
                    }
                  />
                ) : (
                  <div className="checkout-payment-placeholder">
                    PayPal will appear once the shipping address and delivery option are complete.
                  </div>
                )}
              </PayPalScriptProvider>
            )}
          </div>
        </div>

        {/* Right: Summary */}
        <div className="card checkout-summary">
          <h2>Order Summary</h2>
          {selectedRate && (
            <div className="summary-shipping-chip">
              {selectedRate.carrier} {selectedRate.service}
            </div>
          )}
          {items.map(item => (
            <div key={item.id} className="summary-item">
              <div className="summary-item-info">
                <span className="summary-item-name">{item.title}</span>
                <span className="summary-item-qty">× {item.qty}</span>
              </div>
              <span>${(item.price * item.qty).toFixed(2)}</span>
            </div>
          ))}
          <div className="summary-divider" />
          <div className="summary-row"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          <div className="summary-row">
            <span>Shipping</span>
            <span>{selectedRate ? `$${shippingCost.toFixed(2)}` : 'Return to cart'}</span>
          </div>
          <div className="summary-row">
            <span>Estimated tax</span>
            <span>{selectedRate ? `$${taxAmount.toFixed(2)}` : 'Return to cart'}</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-row summary-total">
            <span>Total</span>
            <span>${total.toFixed(2)}{!selectedRate ? '+' : ''}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
